import React from "react";
import { Search } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

const STATUS_STYLES = {
  Active: "bg-green-100 text-green-800",
  Lead: "bg-blue-100 text-blue-800",
  Completed: "bg-slate-100 text-slate-700",
  "Needs Follow-up": "bg-amber-100 text-amber-800",
};

function StatusBadge({ status }) {
  const base = "inline-flex items-center rounded-full px-3 py-1 text-xs font-medium";
  return (
    <span data-testid="status-badge" className={`${base} ${STATUS_STYLES[status] || "bg-slate-100 text-slate-700"}`}>
      {status}
    </span>
  );
}

export default function ClientList({
  clients,
  selectedId,
  query,
  statusFilter,
  loading,
  onQueryChange,
  onStatusFilterChange,
  onSelect,
}) {
  return (
    <Card className="rounded-2xl shadow-sm">
      <CardContent className="space-y-4 p-5">
        <div className="relative">
          <Search className="absolute left-3 top-3 text-slate-400" size={18} />
          <input
            data-testid="search-input"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Search owner, dog, breed, package..."
            className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-10 pr-3 outline-none focus:ring-2 focus:ring-slate-300"
          />
        </div>
        <select
          data-testid="status-filter"
          value={statusFilter}
          onChange={(e) => onStatusFilterChange(e.target.value)}
          className="w-full rounded-2xl border border-slate-200 bg-white p-3 outline-none focus:ring-2 focus:ring-slate-300"
        >
          <option>All</option>
          <option>Lead</option>
          <option>Active</option>
          <option>Needs Follow-up</option>
          <option>Completed</option>
        </select>

        <div className="space-y-3" data-testid="client-list">
          {loading && <p className="rounded-2xl bg-white p-4 text-sm text-slate-500">Loading clients...</p>}
          {!loading &&
            clients.map((client) => (
              <button
                key={client.id}
                data-testid={`client-card-${client.id}`}
                onClick={() => onSelect(client.id)}
                className={`w-full rounded-2xl border p-4 text-left transition hover:bg-slate-50 ${
                  selectedId === client.id
                    ? "border-slate-900 bg-slate-50"
                    : "border-slate-200 bg-white"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold">{client.dogName}</p>
                    <p className="text-sm text-slate-600">{client.ownerName}</p>
                  </div>
                  <StatusBadge status={client.status} />
                </div>
                <p className="mt-2 text-sm text-slate-500">
                  {client.breed} • {client.packageType || "No program yet"}
                  {client.packageType && (client.programPrice > 0 || client.programSessions > 0) && (
                    <span className="ml-1 text-slate-400">
                      ({client.programPrice > 0 ? `$${client.programPrice.toFixed(0)}` : ""}
                      {client.programPrice > 0 && client.programSessions > 0 ? " · " : ""}
                      {client.programSessions > 0 ? `${client.programSessions} sessions` : ""})
                    </span>
                  )}
                </p>
                {client.nextSession && (
                  <p className="mt-2 text-sm font-medium text-slate-700">Next: {client.nextSession}</p>
                )}
              </button>
            ))}
          {!loading && clients.length === 0 && (
            <p className="rounded-2xl bg-white p-4 text-sm text-slate-500">No clients match that search.</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export { StatusBadge };
